import { DateUtil } from '@/utils/date';
import { Avatar } from '@pickleballinc/react-ui';
import { Button } from '@/components/atoms';
import { User } from '@/entities/SystemUsers';

export interface OrganizationContact {
	uuid?: string;
	userUuid: string;
	organizationUuid: string;
	phoneNumber: string | undefined;
	phone: string | undefined;
	email: string | undefined;
	firstName: string | undefined;
	lastName: string | undefined;
	suffixName: string | undefined;
	isPrimary: boolean | undefined;
	showAgeToPublic?: boolean | undefined;
	dob?: string;
	gender?: string;
	singlesRating?: number;
	doublesRating?: number;
	skinnySinglesRating?: number;
	profilePictureUrl?: string;
	countryAbbreviation?: string;
	stateTitle?: string;
	showStateToPublic?: boolean;
	internationalCountryCallingCode?: string;
}

export interface CustomUser extends OrganizationContact {
	state?: string;
	country?: string;
}

type UserCardProps = {
	user: CustomUser | User | Contact;
	isAdding?: boolean;
	showRating?: boolean;
	add: (param: any) => void;
	index?: number;
	addingUser?: string | null;
	renderPayOption?: boolean;
	hideButton?: boolean;
};

export interface Contact {
	uuid?: string | null;
	userUuid: string;
	moduleUuid: string;
	moduleFor: number;
	firstName: string;
	lastName: string;
	suffixName?: string | null;
	isPrimary: boolean;
	dateCreated?: Date | null;
	dateDisabled?: Date | null;
	dateUpdated?: Date | null;
	phoneNumber: string;
	phone?: string;
	email: string;
	showAgeToPublic?: boolean | undefined;
	dob?: string;
	gender?: string;
	singlesRating?: number;
	doublesRating?: number;
	skinnySinglesRating?: number;
	profilePictureUrl?: string;
	countryAbbreviation?: string;
	stateTitle?: string;
	showStateToPublic?: string;
	internationalCountryCallingCode?: string;
}

export function UserCard({ user, add, showRating, addingUser, hideButton }: UserCardProps) {
	const normalizedUser = {
		...user,
		phoneNumber: 'phoneNumber' in user ? user.phoneNumber : user.phone,
		internationalCountryCallingCode: 'internationalCallingCode' in user ? user.internationalCallingCode : ''
	};

	const dobModified = user?.dob ? DateUtil.calculateYearsPassedFrom(new Date(user.dob)) : user?.dob;

	return (
		<>
			{user && (
				<div className="flex w-full flex-row justify-between rounded-lg border border-violet-200 bg-violet-25 p-4 text-start text-sm hover:border-gray-300">
					<div className="flex w-full gap-5 sm:gap-8">
						<div className="flex w-[70px] flex-col items-center gap-2 justify-center">
							<Avatar size="xl" imageUrl={user.profilePictureUrl} />
							<div className="flex flex-col gap-1 text-center">
								{user.showStateToPublic && <p className="text-xs text-gray-800">{user.stateTitle}</p>}
								<p className="text-xs text-gray-400">{user.countryAbbreviation}</p>
							</div>
						</div>
						<div className="relative flex w-full flex-col gap-2">
							<h5 className="text-base font-semibold text-gray-900">
								{user.firstName} {user.lastName}
							</h5>
							<div className="flex flex-wrap justify-between gap-y-2">
								<div className="flex flex-col gap-2">
									{user.dob && (
										<p className="text-xs text-gray-800">
											{`Age: ${user.showAgeToPublic ? DateUtil.calculateYearsPassedFrom(new Date(user.dob)) : '**'}
										| ${user.gender === 'M' ? 'Male' : 'Female'}`}
										</p>
									)}
									<p className="text-xs text-gray-800 text-wrap break-all">{user.email}</p>
									<p className="text-xs text-gray-800 text-wrap break-all">
										{normalizedUser.internationalCountryCallingCode + ' ' + normalizedUser.phoneNumber}
									</p>
									{showRating && (
										<div className="flex gap-2">
											<p
												className={`text-xs ${user.doublesRating && user.doublesRating > 0 ? 'text-gray-800' : 'text-gray-500'}`}
											>
												{user.doublesRating && user.doublesRating > 0 ? `(D) ${user.doublesRating.toFixed(2)}` : ''}
											</p>
											<p
												className={`text-xs ${user.singlesRating && user.singlesRating > 0 ? 'text-gray-800' : 'text-gray-500'}`}
											>
												{user.singlesRating && user.singlesRating > 0 ? `(S) ${user.singlesRating.toFixed(2)}` : ''}
											</p>
											<p
												className={`text-xs ${user?.skinnySinglesRating && user?.skinnySinglesRating > 0 ? 'text-gray-800' : 'text-gray-500'}`}
											>
												{user.skinnySinglesRating && user.skinnySinglesRating > 0
													? `(SS) ${user.skinnySinglesRating.toFixed(2)}`
													: ''}
											</p>
										</div>
									)}
								</div>
							</div>
						</div>
						{!hideButton ? (
							<div className="flex items-end gap-2">
								<Button
									className="w-fit bg-orange-500 !px-2.5 !py-2 !h-fit !rounded-md"
									onClick={() => add({ ...user, dob: dobModified })}
									disabled={!!addingUser && addingUser === user.uuid}
								>
									<p className="text-xs font-semibold text-white">
										{addingUser && addingUser === user.uuid ? <div>Loading...</div> : 'Select'}
									</p>
								</Button>
							</div>
						) : (
							<div className="flex items-end gap-2">
								<Button
									className="w-fit bg-gray-600 !px-2.5 !py-2 !h-fit !rounded-md cursor-not-allowed"
									disabled={!!addingUser && addingUser === user.uuid}
								>
									<p className="text-xs font-semibold text-white">
										{addingUser && addingUser === user.uuid ? <div>Loading...</div> : 'Selected'}
									</p>
								</Button>
							</div>
						)}
					</div>
				</div>
			)}
		</>
	);
}
